import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { actions } from '../Users/redux/actions';
import Strings from '../../../Strings';

function OrganizationDetail(props) {
  const { match, getOrganizationDetails, editOrganization, onNavigate } = props;
  const orgId = match?.params?.orgId;

  const [organization, setOrganization] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedOrg, setEditedOrg] = React.useState({});

  React.useEffect(() => {
    if (orgId && getOrganizationDetails) {
      setIsLoading(true);
      setError(null);
      getOrganizationDetails(orgId)
        .then(resp => {
          const orgData = resp?.response;
          if (orgData) {
            setOrganization(orgData);
          } else {
            setError('Organization not found');
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load organization:', err);
          setError('Failed to load organization');
          setIsLoading(false);
        });
    }
  }, [orgId, getOrganizationDetails]);

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      setEditedOrg({});
    } else {
      setIsEditing(true);
      setEditedOrg({ ...organization });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedOrg(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!editOrganization || !organization) return;

    setIsLoading(true);
    setError(null);

    try {
      const updateData = {
        name: editedOrg.name || organization.name,
        type: editedOrg.type || organization.type,
        city: editedOrg.city || organization.city,
        state: editedOrg.state || organization.state,
        contact_email: editedOrg.contact_email || organization.contact_email,
        contact_phone: editedOrg.contact_phone || organization.contact_phone,
      };

      await editOrganization(orgId, updateData);
      // Reload organization data
      const resp = await getOrganizationDetails(orgId);
      if (resp?.response) {
        setOrganization(resp.response);
      }
      setIsEditing(false);
      setEditedOrg({});
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to update organization:', err);
      setError('Failed to update organization');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('/admin/organizations');
    }
  };

  if (isLoading && !organization) {
    return (
      <div className="organization-detail-page" style={{ padding: '20px' }}>
        <div className="loading">{Strings.loading || 'Loading organization details...'}</div>
      </div>
    );
  }

  if (error && !organization) {
    return (
      <div className="organization-detail-page" style={{ padding: '20px' }}>
        <div className="error-message" style={{ color: 'red' }}>
          <p>{error}</p>
        </div>
        <button className="btn-back" onClick={handleBack}>
          {Strings.backToOrganizations || 'Back to Organizations'}
        </button>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="organization-detail-page" style={{ padding: '20px' }}>
        <div className="error-message">
          <p>{Strings.organizationNotFound || 'Organization not found'}</p>
        </div>
        <button className="btn-back" onClick={handleBack}>
          {Strings.backToOrganizations || 'Back to Organizations'}
        </button>
      </div>
    );
  }

  const displayOrg = isEditing ? { ...organization, ...editedOrg } : organization;

  return (
    <div className="organization-detail-page" style={{ padding: '20px' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <button className="btn-back" onClick={handleBack} style={{ marginRight: '20px', cursor: 'pointer' }}>
            ← {Strings.backToOrganizations || 'Back to Organizations'}
          </button>
          <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {organization.name}
          </span>
        </div>
        <div className="header-actions">
          {!isEditing ? (
            <button className="btn-edit" onClick={handleEditToggle} style={{ padding: '8px 16px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', lineHeight: 1 }}>
              {Strings.editOrganization || 'Edit Organization'}
            </button>
          ) : (
            <>
              <button className="btn-cancel" onClick={handleEditToggle} disabled={isLoading} style={{ padding: '8px 16px', marginRight: '10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', lineHeight: 1 }}>
                {Strings.cancel || 'Cancel'}
              </button>
              <button className="btn-save" onClick={handleSave} disabled={isLoading} style={{ padding: '8px 16px', cursor: 'pointer', backgroundColor: '#4CAF50', color: 'white', border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', lineHeight: 1 }}>
                {isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          <p>{error}</p>
        </div>
      )}

      <div className="organization-detail-content">
        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.basicInformation || 'Basic Information'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.id || 'ID'}</label>
              <div className="field-value">{organization.id}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.uuid || 'UUID'}</label>
              <div className="field-value">{organization.uuid}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.name || 'Name'}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={displayOrg.name || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{organization.name}</div>
              )}
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.type || 'Type'}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="type"
                  value={displayOrg.type || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{organization.type || 'N/A'}</div>
              )}
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.orgGroup || 'Org Group'}</label>
              <div className="field-value">{organization.org_group_name || 'N/A'}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.orgGroupId || 'Org Group ID'}</label>
              <div className="field-value">{organization.org_group_id || 'N/A'}</div>
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.location || 'Location'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.city || 'City'}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="city"
                  value={displayOrg.city || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{organization.city || 'N/A'}</div>
              )}
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.state || 'State'}</label>
              {isEditing ? (
                <input
                  type="text"
                  name="state"
                  value={displayOrg.state || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{organization.state || 'N/A'}</div>
              )}
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.address || 'Address'}</label>
              <div className="field-value">{organization.address || 'N/A'}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.zipCode || 'Zip Code'}</label>
              <div className="field-value">{organization.zip_code || 'N/A'}</div>
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.contactInformation || 'Contact Information'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.contactEmail || 'Contact Email'}</label>
              {isEditing ? (
                <input
                  type="email"
                  name="contact_email"
                  value={displayOrg.contact_email || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{organization.contact_email || 'N/A'}</div>
              )}
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.contactPhone || 'Contact Phone'}</label>
              {isEditing ? (
                <input
                  type="tel"
                  name="contact_phone"
                  value={displayOrg.contact_phone || ''}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px' }}
                />
              ) : (
                <div className="field-value">{organization.contact_phone || 'N/A'}</div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-section" style={{ marginBottom: '30px' }}>
          <h2>{Strings.timestamps || 'Timestamps'}</h2>
          <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '15px' }}>
            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.createdAt || 'Created At'}</label>
              <div className="field-value">{organization.created_at || 'N/A'}</div>
            </div>

            <div className="detail-field">
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.updatedAt || 'Updated At'}</label>
              <div className="field-value">{organization.updated_at || 'N/A'}</div>
            </div>
          </div>
        </div>

        {organization.config && (
          <div className="detail-section" style={{ marginBottom: '30px' }}>
            <h2>{Strings.configuration || 'Configuration'}</h2>
            <div className="detail-grid" style={{ marginTop: '15px' }}>
              <div className="detail-field">
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>{Strings.configJson || 'Config JSON'}</label>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '4px', overflow: 'auto', maxHeight: '300px' }}>
                  {typeof organization.config === 'string'
                    ? JSON.stringify(JSON.parse(organization.config), null, 2)
                    : JSON.stringify(organization.config, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const mapDispatchToProps = dispatch => ({
  getOrganizationDetails: orgId => dispatch(actions.getOrganizationDetails(orgId)),
  editOrganization: (orgId, data) => dispatch(actions.editOrganization(orgId, data)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(null, mapDispatchToProps)(OrganizationDetail);
